import { sortCurrencies } from './webledgerCurrencyOrder.js';
import type { DocCache } from '@colplat/core-client';
import { currencyChipHtml, escapeHtml, formatNum, type MoneyCurrency } from '@colplat/ui-web';
import { readCached } from '../offlineReads.js';
import { PREF, type PrefsHandle } from './webledgerPrefs.js';

/**
 * The chip tally — legacy's Cmd/Ctrl-click running selection.
 *
 * Every signed amount in this app is a currency chip, and the chip carries
 * `data-cur`/`data-amt` for exactly this: Cmd/Ctrl-click any of them, on any
 * page, and they add up in a small panel that follows you around. Shift as
 * well and the chip subtracts instead. It is the answer to "what do these
 * four rows come to", asked of a list that has no way to select rows —
 * arithmetic done where the numbers already are, rather than in a calculator
 * beside the screen.
 *
 * Three things make it feel reliable rather than clever, and each is a
 * decision rather than an accident:
 *
 *  - **A pick is (signature, position), not an element.** Keyed by
 *    `currency|amount` plus which of the identically-labelled chips it was,
 *    so a list that re-renders — and these lists re-render constantly, on
 *    every lease event — keeps its selection on the same numbers. Keyed by
 *    element it would evaporate the moment a colleague opened a transaction.
 *  - **It persists in the user's preferences**, not `localStorage`, so it
 *    survives the navigation between webledger's pages (each is a full page
 *    load) and is the same on a laptop as on a desktop. Legacy writes both
 *    and treats the server copy as the durable one; one store is simpler and
 *    is what the rest of this app already does.
 *  - **It expires after twelve hours.** A scratch selection is a working
 *    thought, and finding yesterday's still highlighted is unsettling in a
 *    ledger — you cannot tell at a glance whether you meant it.
 */

/** A working day. Long enough to survive lunch and a reboot, short enough that yesterday's thinking does not reappear. */
const TTL_MS = 12 * 60 * 60 * 1000;

interface StoredTally {
  at: number;
  /** `{ "EUR|100": { "0": 1, "2": -1 } }` — Maps do not survive JSON. */
  picks: Record<string, Record<string, number>>;
}

export interface ChipTallyOptions {
  root: HTMLElement;
  prefs: PrefsHandle;
  docCache: DocCache;
  token: string;
}

interface FxResponse {
  base: string;
  rates: Record<string, number>;
  stale: boolean;
  totalsCurrencies: Array<{ code: string; name: string }>;
}

/** `currency|amount`, the identity a chip shares with every other chip showing the same money. */
function signatureOf(chip: HTMLElement): string {
  return `${chip.dataset['cur'] ?? ''}|${chip.dataset['amt'] ?? '0'}`;
}

function signatureParts(signature: string): { code: string; amount: number } {
  const at = signature.indexOf('|');
  return { code: signature.slice(0, at), amount: Number(signature.slice(at + 1)) || 0 };
}

export function mountChipTally(opts: ChipTallyOptions): void {
  const { root, prefs, docCache, token } = opts;
  /** signature -> (position -> +1 | -1). */
  const picks = new Map<string, Map<number, number>>();
  /**
   * Currency metadata, read from the cache every page has already primed.
   *
   * Fetched here rather than passed in, so mounting the tally is one call
   * with no per-page argument to forget. Until it lands the panel formats at
   * two decimals, which is right for most currencies and briefly wrong for
   * HUF — a repaint fixes it as soon as the cached read resolves.
   */
  const moneyByCode = new Map<string, MoneyCurrency>();
  let mode = prefs.get(PREF.tallyMode) ?? 'by';
  let fx: FxResponse | undefined;
  let fxRequested = false;
  /** The last markup written, so a re-render that changes nothing does not touch the DOM. */
  let painted: string | undefined;
  /** Which currency's ingredients are open, if any. */
  let openDetail: string | undefined;

  restore();
  const panel = buildPanel(root);
  wire();
  apply();
  void (async () => {
    const read = await readCached<{ currencies: MoneyCurrency[] }>(
      docCache, 'currencies', '/apps/webledger/currencies', token,
    );
    if (!read.ok) return;
    for (const currency of read.data.currencies) moneyByCode.set(currency.code, currency);
    painted = undefined;
    render();
  })();

  // ------------------------------------------------------------ persistence

  function restore(): void {
    const raw = prefs.get(PREF.tally);
    if (!raw) return;
    let stored: StoredTally;
    try {
      stored = JSON.parse(raw) as StoredTally;
    } catch {
      return;   // a malformed preference costs an empty tally, never an error
    }
    if (!stored || typeof stored.at !== 'number' || Date.now() - stored.at > TTL_MS) return;
    for (const [signature, slots] of Object.entries(stored.picks ?? {})) {
      const map = new Map<number, number>();
      for (const [slot, sign] of Object.entries(slots)) {
        if (sign === 1 || sign === -1) map.set(Number(slot), sign);
      }
      if (map.size > 0) picks.set(signature, map);
    }
  }

  function save(): void {
    if (picks.size === 0) {
      prefs.set(PREF.tally, '');
      return;
    }
    const plain: Record<string, Record<string, number>> = {};
    for (const [signature, slots] of picks) plain[signature] = Object.fromEntries(slots);
    prefs.set(PREF.tally, JSON.stringify({ at: Date.now(), picks: plain } satisfies StoredTally));
  }

  // ------------------------------------------------------------- selection

  /** Every chip on the page except the tally's own, which would otherwise be pickable and self-referential. */
  function chips(): HTMLElement[] {
    return [...root.querySelectorAll<HTMLElement>('.cf-cur-chip[data-cur]')]
      .filter((chip) => !panel.contains(chip));
  }

  /**
   * Which of the identically-labelled chips is this one?
   *
   * Position among its own signature rather than among all chips, so adding a
   * row of a different amount above it does not move every pick below.
   */
  function slotOf(chip: HTMLElement, all: HTMLElement[]): number {
    const signature = signatureOf(chip);
    let index = -1;
    for (const candidate of all) {
      if (signatureOf(candidate) !== signature) continue;
      index += 1;
      if (candidate === chip) return index;
    }
    return Math.max(0, index);
  }

  function toggle(chip: HTMLElement, sign: 1 | -1): void {
    const all = chips();
    const signature = signatureOf(chip);
    const slot = slotOf(chip, all);
    const slots = picks.get(signature) ?? new Map<number, number>();
    // The same modifier again releases the pick; the other modifier flips its
    // sign rather than releasing — which is what "I meant minus" needs.
    if (slots.get(slot) === sign) slots.delete(slot);
    else slots.set(slot, sign);
    if (slots.size > 0) picks.set(signature, slots);
    else picks.delete(signature);
    save();
    apply(all);
  }

  function clear(): void {
    picks.clear();
    openDetail = undefined;
    save();
    apply();
  }

  /** Repaint the highlights and the panel. Cheap enough to call on every render. */
  function apply(all?: HTMLElement[]): void {
    const seen = new Map<string, number>();
    for (const chip of all ?? chips()) {
      const signature = signatureOf(chip);
      const slot = (seen.get(signature) ?? -1) + 1;
      seen.set(signature, slot);
      const sign = picks.get(signature)?.get(slot);
      chip.classList.toggle('cf-cur-chip--picked', sign !== undefined);
      chip.classList.toggle('cf-cur-chip--minus', sign === -1);
    }
    render();
  }

  // ----------------------------------------------------------------- totals

  interface Part { amount: number; count: number; sign: 1 | -1 }
  interface Bucket { total: number; count: number; parts: Part[] }

  function totals(): Map<string, Bucket> {
    const by = new Map<string, Bucket>();
    for (const [signature, slots] of picks) {
      const { code, amount } = signatureParts(signature);
      const bucket = by.get(code) ?? { total: 0, count: 0, parts: [] };
      let plus = 0;
      let minus = 0;
      for (const sign of slots.values()) { if (sign === 1) plus += 1; else minus += 1; }
      if (plus > 0) {
        bucket.total += amount * plus;
        bucket.count += plus;
        bucket.parts.push({ amount, count: plus, sign: 1 });
      }
      if (minus > 0) {
        bucket.total -= amount * minus;
        bucket.count += minus;
        bucket.parts.push({ amount, count: minus, sign: -1 });
      }
      by.set(code, bucket);
    }
    for (const bucket of by.values()) {
      bucket.parts.sort((a, b) => b.sign - a.sign || Math.abs(b.amount) - Math.abs(a.amount));
    }
    return by;
  }

  function money(code: string): MoneyCurrency {
    return moneyByCode.get(code) ?? { fraction: 2, code };
  }

  /**
   * Rates, fetched only when a conversion is actually asked for.
   *
   * The reports page loads them anyway; the register and the chart of
   * accounts do not, and making every page pay for a feature most visits
   * never use is the wrong default. Failing leaves the tally per-currency,
   * which is the honest fallback rather than a wrong number.
   */
  function ensureFx(): void {
    if (fx || fxRequested) return;
    fxRequested = true;
    void (async () => {
      const read = await readCached<FxResponse>(docCache, 'fx', '/apps/webledger/fx', token);
      if (read.ok) {
        fx = read.data;
        painted = undefined;   // the mode list just changed
        render();
      }
    })();
  }

  // ------------------------------------------------------------------- view

  function render(): void {
    if (picks.size === 0) {
      panel.hidden = true;
      panel.innerHTML = '';
      painted = undefined;
      return;
    }
    ensureFx();
    const by = totals();
    const codes = sortCurrencies(by.keys());

    const rows = mode === 'by' || !fx
      ? codes.map((code) => rowHtml(code, by.get(code)!)).join('')
      : convertedRowHtml(by, codes);

    const options = ['<option value="by">By currency</option>']
      .concat((fx?.totalsCurrencies ?? []).map((c) => `<option value="${escapeHtml(c.code)}">In ${escapeHtml(c.code)}</option>`))
      .join('');

    const html = `
      <div class="wl-tally__head">
        <span class="cf-h">Tally</span>
        <button type="button" class="wl-tally__x" data-tally-clear
          aria-label="Clear the tally" title="Clear the tally">&#10005;</button>
      </div>
      <div class="wl-tally__rows">${rows}</div>
      <select class="form-select form-select-sm mt-1" data-tally-mode aria-label="Tally currency">${options}</select>
      <p class="wl-tally__hint">Cmd/Ctrl-click an amount to add it, Shift too to subtract.</p>`;

    // This runs on every list re-render; rewriting identical markup would
    // close the open ingredient list and fight the reader's own clicks.
    if (html !== painted) {
      panel.innerHTML = html;
      painted = html;
      const select = panel.querySelector<HTMLSelectElement>('[data-tally-mode]')!;
      // The select falls back to "By currency" when the remembered currency
      // is not in the list yet — but `mode` is deliberately *not* overwritten
      // with that fallback. The list is empty until the rates arrive, which
      // is one asynchronous read after mount, and assigning here would forget
      // a remembered currency on every single page load. `render()` already
      // treats a missing `fx` as per-currency, so the two agree meanwhile.
      select.value = [...select.options].some((o) => o.value === mode) ? mode : 'by';
    }
    panel.hidden = false;
  }

  function rowHtml(code: string, bucket: Bucket): string {
    const open = openDetail === code;
    return `<div class="wl-tally__row">
      <button type="button" class="wl-tally__line" data-tally-detail="${escapeHtml(code)}"
        aria-expanded="${open}" title="What went into this">
        ${currencyChipHtml(String(bucket.total), code, { currency: money(code) })}
        <span class="wl-tally__n">${bucket.count}&times;</span>
      </button>
      ${open ? `<div class="wl-tally__parts">${partsHtml(code, bucket.parts)}</div>` : ''}
    </div>`;
  }

  /**
   * One line, everything converted into the chosen currency.
   *
   * A missing rate is called out rather than quietly dropped: a total that
   * looks complete and is not is the worst thing a tally can show.
   */
  function convertedRowHtml(by: Map<string, Bucket>, codes: string[]): string {
    let sum = 0;
    let count = 0;
    let missing = false;
    for (const code of codes) {
      const bucket = by.get(code)!;
      count += bucket.count;
      const converted = convert(bucket.total, code, mode);
      if (converted === undefined) missing = true;
      else sum += converted;
    }
    const open = openDetail === '*';
    return `<div class="wl-tally__row">
      <button type="button" class="wl-tally__line" data-tally-detail="*" aria-expanded="${open}"
        title="What went into this">
        ${currencyChipHtml(String(sum), mode, { currency: money(mode) })}
        ${missing ? '<span class="wl-fx-warn" title="Some of these currencies have no rate and are not included">⚠</span>' : ''}
        <span class="wl-tally__n">${count}&times;</span>
      </button>
      ${open
        ? `<div class="wl-tally__parts">${codes.map((code) =>
          `<div class="wl-tally__sub">${escapeHtml(code)}</div>${partsHtml(code, by.get(code)!.parts)}`).join('')}</div>`
        : ''}
    </div>`;
  }

  /**
   * What went into a line, always in the currency it was picked in — even
   * when the total above is converted. Converting the ingredients too would
   * hide what was actually selected, which is the one thing this list is for.
   */
  function partsHtml(code: string, parts: Part[]): string {
    return parts.map((part) => `<div class="wl-tally__part">
      <span class="${part.sign < 0 ? 'text-danger' : 'text-success'}">${part.sign < 0 ? '&minus;' : '+'}</span>
      <span>${escapeHtml(formatNum(Math.abs(part.amount), money(code)))}</span>
      <span class="text-secondary">${part.count > 1 ? `&times;${part.count}` : ''}</span>
    </div>`).join('');
  }

  function convert(amount: number, from: string, to: string): number | undefined {
    if (from === to) return amount;
    const rates = fx?.rates ?? {};
    const fromRate = rates[from];
    const toRate = rates[to];
    if (!fromRate || !toRate) return undefined;
    // Rates are quoted against one base, so crossing two of them is the
    // conversion — same arithmetic the reports' totals bar does.
    return (amount / fromRate) * toRate;
  }

  // ------------------------------------------------------------------ wiring

  /**
   * How long a press has to last to mean "subtract".
   *
   * Long enough not to fire while somebody is deciding whether to scroll,
   * short enough that it does not feel broken. The same 500ms the platform
   * itself uses for a context menu, which is what people's thumbs are already
   * calibrated to.
   */
  const LONG_PRESS_MS = 500;
  /** Past this much movement it was a scroll, not a press. */
  const SLOP_PX = 10;

  function wire(): void {
    // Capture phase, because a chip usually sits inside something clickable —
    // a register row that opens an editor, a report card that marks itself.
    // A modified click means the tally and nothing else.
    root.addEventListener('click', (event) => {
      const chip = (event.target as HTMLElement).closest<HTMLElement>('.cf-cur-chip[data-cur]');
      /*
       * A touch gesture has already done the work by the time the click
       * arrives; swallowing it is what stops the row underneath opening as
       * well. `click` still fires after `pointerup` on touch, and without
       * this every tally tap would also open a transaction.
       */
      if (chip && handledByTouch.has(chip)) {
        handledByTouch.delete(chip);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const mouse = event as MouseEvent;
      if (!mouse.metaKey && !mouse.ctrlKey) return;
      if (!chip || panel.contains(chip)) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(chip, mouse.shiftKey ? -1 : 1);
    }, true);

    /*
     * Touch has no Cmd and no Shift, so the gesture carries the modifier: a
     * tap adds, a long press subtracts.
     *
     * Only for a `touch` pointer. A mouse keeps Cmd-click exactly as it was —
     * a bare click on a chip there still belongs to whatever the chip sits
     * inside, and stealing it would break opening a row by clicking its
     * amount, which is a thing people do.
     *
     * Nothing here calls `preventDefault` on the pointer events themselves.
     * That would cancel scrolling, and a list you cannot scroll because every
     * amount in it is a gesture target is a worse list.
     */
    const handledByTouch = new WeakSet<HTMLElement>();
    let pressTimer: ReturnType<typeof setTimeout> | undefined;
    let pressChip: HTMLElement | undefined;
    let pressAt: { x: number; y: number } | undefined;

    const endPress = (): void => {
      if (pressTimer !== undefined) clearTimeout(pressTimer);
      pressTimer = undefined;
      pressChip = undefined;
      pressAt = undefined;
    };

    root.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      const chip = (event.target as HTMLElement).closest<HTMLElement>('.cf-cur-chip[data-cur]');
      if (!chip || panel.contains(chip)) return;
      pressChip = chip;
      pressAt = { x: event.clientX, y: event.clientY };
      pressTimer = setTimeout(() => {
        pressTimer = undefined;
        if (!pressChip) return;
        toggle(pressChip, -1);
        handledByTouch.add(pressChip);
        // A long press gives no other feedback on a touch screen; the panel
        // updating is out of the thumb's line of sight.
        navigator.vibrate?.(15);
        pressChip = undefined;
        pressAt = undefined;
      }, LONG_PRESS_MS);
    }, true);

    root.addEventListener('pointermove', (event) => {
      if (!pressAt) return;
      const moved = Math.abs(event.clientX - pressAt.x) + Math.abs(event.clientY - pressAt.y);
      // They are scrolling the list, not pressing the number on it.
      if (moved > SLOP_PX) endPress();
    }, true);

    root.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch' || !pressChip) { endPress(); return; }
      // Released before the long press matured: an ordinary tap, which adds.
      if (pressTimer !== undefined) {
        toggle(pressChip, 1);
        handledByTouch.add(pressChip);
      }
      endPress();
    }, true);

    root.addEventListener('pointercancel', endPress, true);

    panel.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-tally-clear]')) { clear(); return; }
      const line = target.closest<HTMLElement>('[data-tally-detail]');
      if (line) {
        const code = line.dataset['tallyDetail']!;
        openDetail = openDetail === code ? undefined : code;
        painted = undefined;
        render();
      }
    });
    panel.addEventListener('change', (event) => {
      const select = (event.target as HTMLElement).closest<HTMLSelectElement>('[data-tally-mode]');
      if (!select) return;
      mode = select.value;
      prefs.set(PREF.tallyMode, mode);
      openDetail = undefined;
      painted = undefined;
      render();
    });
  }
}

/**
 * The panel, appended once per page.
 *
 * Fixed to the bottom corner rather than placed in the layout: it belongs to
 * no one page's structure, it must not move anything when it appears
 * (invariant #11's second half), and the numbers it sums are wherever the
 * reader happens to be scrolled.
 */
function buildPanel(root: HTMLElement): HTMLElement {
  const existing = root.querySelector<HTMLElement>('#wlChipTally');
  if (existing) return existing;
  const panel = document.createElement('aside');
  panel.id = 'wlChipTally';
  panel.className = 'wl-tally';
  panel.hidden = true;
  panel.setAttribute('aria-label', 'Selected amounts');
  root.append(panel);
  return panel;
}
